import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { catchError, concatMap, map } from 'rxjs/operators';
import { Observable, EMPTY, of, throwError } from 'rxjs';
import { LogService } from '../../@core/services/logger.service';
import { LogLevel } from '../../@core/services/logger.service';
import { ToastrService } from 'ngx-toastr';
import { RouteStatus } from '../models/dto-routestatus';
import { OptimizationMode } from '../models/dto-optimizationmode';
import { LiveRouteConsol } from '../models/live-route-consol';
import { RouteStops } from '../models/dto-route-stops';
import { Stop } from '../models/live-route';
import { DTOOptimizationResult } from '../models/dto-optimizationresult';

export interface RouteOptimizationEndpoint {
  address: string;
  latitude: number;
  longitude: number;
}

export interface RouteOptimizationEndpoints {
  endAtLastStop?: boolean;
  start: RouteOptimizationEndpoint | null;
  end: RouteOptimizationEndpoint | null;
  options?: RouteOptimizationEditableOptions;
}

export interface RouteOptimizationEditableOptions {
  routingMode: string;
  trafficEnabled: boolean;
  usePriority: boolean;
  departureTime: string;
  stopDurationMinutes: number;
}

export interface RouteOptimizationPreview {
  routeDate: string;
  engine: string;
  departure: string;
  options: RouteOptimizationEditableOptions;
  stops: {
    groupId: number | null; stopNumber: number | null; priority: number;
    address: string; latitude: number | null; longitude: number | null; included: boolean;
  }[];
}

export interface RouteOptimizationEndpointsResponse extends RouteOptimizationEndpoints {
  depotAddress: string;
  depotLatitude?: number | null;
  depotLongitude?: number | null;
  parameters?: RouteOptimizationParameters;
  preview?: RouteOptimizationPreview;
}

export interface RouteOptimizationConfiguredParameters {
  routingMode: string | null;
  trafficMode: boolean | null;
  usePriority: boolean | null;
  departure: string | null;
  transportModeId: number | null;
  transportModeName: string | null;
  stopDuration: number | null;
  limiteWeight: number | null;
  breakTimes: number | null;
  avoidTurns: boolean | null;
}

export interface RouteOptimizationEffectiveParameters {
  waypointStopLimit: number;
  waypointRoutingMode: string;
  polylineRoutingMode: string | null;
  trafficEnabled: boolean;
  usePriority: boolean;
  departure: string;
  tourPlanningDeparture: string;
  tourPlanningStopDurationMinutes: number;
  transportMode: string;
}

export interface RouteOptimizationParameters {
  hasSavedParameters: boolean;
  configured: RouteOptimizationConfiguredParameters;
  current: RouteOptimizationEffectiveParameters;
  afterEndpointSave: RouteOptimizationEffectiveParameters;
}

@Injectable({
  providedIn: 'root'
})
export class OptimizationService {
  lat: number = 0;
  lng: number = 0;
  private _baseUrl: string = environment.apiUrl;
  private _httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: '*NationexInternalAPI*'
    })
  };
  constructor(
    private http: HttpClient,
    private logger: LogService,
    private toastservice: ToastrService
  ) {}

  /*
   * Build the api url call with the provided method name
   */
  private getApiUrlCall(methodName: string): string {
    let url = '';

    url = this._baseUrl + methodName;

    return url;
  }

  private getApiUrlCallV5(methodName: string): string {
    let url = '';

    url = environment.apiUrlV5 + methodName;

    return url;
  }

  private formatRouteDate(routeDate: Date): string {
    const year = routeDate.getFullYear();
    const month = String(routeDate.getMonth() + 1).padStart(2, '0');
    const day = String(routeDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  public getRouteEndpoints(routeId: number, routeDate?: Date, includeStops = false): Observable<RouteOptimizationEndpointsResponse> {
    const query = [];
    if (routeDate) { query.push(`routeDate=${this.formatRouteDate(routeDate)}`); }
    if (includeStops) { query.push('includeStops=true'); }
    const dateQuery = query.length ? '?' + query.join('&') : '';
    return this.http.get<RouteOptimizationEndpointsResponse>(
      this.getApiUrlCallV5(`api/Optimization/Routes/${routeId}/Endpoints${dateQuery}`)
    );
  }

  public saveRouteEndpoints(routeId: number, endpoints: RouteOptimizationEndpoints): Observable<void> {
    return this.http.put<void>(
      this.getApiUrlCallV5(`api/Optimization/Routes/${routeId}/Endpoints`),
      endpoints
    );
  }

  public SetRouteStatus(
    routeDate: Date,
    routeID: string,
    status: string
  ): Observable<RouteStatus> {
    const url = this.getApiUrlCall('api/v3/LiveRoutes/UpdateRouteStatus');
    const dtoroutestatus: RouteStatus = new RouteStatus();
    dtoroutestatus.RouteDate = routeDate;
    dtoroutestatus.RouteId = routeID;
    dtoroutestatus.RouteStatus = status;
    const response = this.http
      .put<RouteStatus>(url, JSON.stringify(dtoroutestatus), this._httpOptions)
      .pipe(catchError(this.errorHandler.bind(this)));

    return response;
  }


// VERSION V3 - Optimise pour la distance uniquement
public optimizeRouteForDistance(
  routedate: Date,
  routeid: string
): Observable<number> {
  const url = this.getApiUrlCall('api/v3/Optimization/OptimizeRouteForDistance');
  const dtooptimizationmode: OptimizationMode = new OptimizationMode();
  dtooptimizationmode.RouteDate = routedate;
  dtooptimizationmode.RouteId = routeid;
  dtooptimizationmode.Mode = '3';
  dtooptimizationmode.Latitude = this.lat;
  dtooptimizationmode.Longitude = this.lng;

  const response = this.http
    .put<number>(
      url,
      JSON.stringify(dtooptimizationmode, this.replacer),
      this._httpOptions
    )
    .pipe(catchError(this.errorHandler.bind(this)));

  return response;
}

  public ReoptimizeRoute(
    routedate: Date,
    routeid: string
  ): Observable<DTOOptimizationResult> {
    // this.SetOptimizationMode(routedate, routeid, '3').subscribe(response1 => {
    // });
    const url = this.getApiUrlCall(
      'api/v3/Optimization/OptimizeRouteWithResult'
    );
    const dtooptimizationmode: OptimizationMode = new OptimizationMode();
    dtooptimizationmode.RouteDate = routedate;
    dtooptimizationmode.RouteId = routeid;
    dtooptimizationmode.Mode = '3';
    dtooptimizationmode.Latitude = this.lat;
    dtooptimizationmode.Longitude = this.lng;

    const response = this.http
      .put<DTOOptimizationResult>(
        url,
        JSON.stringify(Object.assign({}, dtooptimizationmode, {
          RouteDate: this.formatRouteDate(routedate)
        })),
        this._httpOptions
      )
      .pipe(
        concatMap((result: DTOOptimizationResult) => {
          if (result.IsError) {
            return of(result);
          }

          return this.GenerateRoutePolylineApiV5(routedate, Number(routeid))
            .pipe(map((success: boolean) => {
              if (!success) {
                throw new Error('Route polyline generation failed.');
              }
              return result;
            }));
        }),
        catchError(this.reportErrorAndRethrow.bind(this))
      );
    return response;
  }


  public OptimizeRouteApiV5(
    routedate: Date,
    routeid: number
  ): Observable<boolean> {
    // this.SetOptimizationMode(routedate, routeid, '3').subscribe(response1 => {
    // });
    const url = this.getApiUrlCallV5(
      'api/Optimization/OptimizeRoute'
    );

    const response = this.http
      .post<boolean>(
        url,
        { RouteDate: this.formatRouteDate(routedate), RouteId: routeid }
      )
      .pipe(
        concatMap((success: boolean) => {
          if (!success) {
            return throwError(new Error('Optimization failed.'));
          }

          return this.GenerateRoutePolylineApiV5(routedate, routeid)
            .pipe(map(() => success));
        }),
        catchError(this.reportErrorAndRethrow.bind(this))
      );
    return response;
  }

  private GenerateRoutePolylineApiV5(
    routedate: Date,
    routeid: number
  ): Observable<boolean> {
    const url = this.getApiUrlCallV5('api/Optimization/OptimizeRoutePolyline');
    return this.http.post<boolean>(url, {
      RouteDate: this.formatRouteDate(routedate),
      RouteId: routeid
    }).pipe(
      map(success => {
        if (!success) {
          throw new Error('Route polyline generation failed.');
        }
        return success;
      })
    );
  }


  public replacer(key, value) {
    if (this[key] instanceof Date) {
      return this[key].toISOString().slice(0, 10);
    }
    return value;
  }

  public GetRouteStopEta(
    routedate: Date,
    routeid: string
  ): Observable<boolean> {
    const url = this.getApiUrlCall('api/v3/Optimization/CalculateRouteETA');
    const dtooptimizationmode: OptimizationMode = new OptimizationMode();
    dtooptimizationmode.RouteDate = routedate;
    dtooptimizationmode.RouteId = routeid;
    dtooptimizationmode.Mode = '3';

    const response = this.http
      .put<boolean>(
        url,
        JSON.stringify(dtooptimizationmode, this.replacer),
        this._httpOptions
      )
      .pipe(catchError(this.errorHandler.bind(this)));
    return response;
  }

  /**
   * Creates a toast, logs error and returns EMPTY
   */
  private errorHandler(error: HttpErrorResponse) {
    this.logger.localstorage(LogLevel.Error, error.message);
    this.toastservice.error(error.message, 'Erreur: API', {
      positionClass: 'toast-bottom-left',
      disableTimeOut: true
    });

    return EMPTY;
  }

  private reportErrorAndRethrow(error: any) {
    this.errorHandler(error as HttpErrorResponse);
    return throwError(error);
  }
}
